import { RadarChart } from "@components/charts/RadarChart";
import { ExportButton } from "@components/exports/ExportButton";
import {
  EXPORT_INTERVIEW_PACK_TO_GOOGLE_DOCS,
  GENERATE_INTERVIEW_PACK,
} from "@lib/graphql/mutations";
import { SanitizedHtml } from "@lib/SanitizedHtml";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  Sparkles,
} from "lucide-react";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useMutation } from "urql";

interface CompetencyScore {
  dimension: string | null;
  // Story #2255 — snapshot carries continuity_id + ideal_score per entry so
  // both the in-app pack view and the exported doc can render the radar
  // chart directly from the frozen content (no blueprint round-trip).
  continuity_id?: string | null;
  ideal_score?: number | null;
  score: number | null;
  anchor: string | null;
  band_level: string | null;
  band_body: string | null;
  evidence: string | null;
}

interface MatrixBand {
  dimension: string | null;
  level: string | null;
  body: string | null;
}

// The composed pack snapshot (InterviewPacks::ComposePack). Every section is
// optional — a light pack omits the assessment matrix, and any source may be
// absent — so each field is nullable.
export interface InterviewPackContent {
  profile?: string | null;
  role_overview?: {
    role_title?: string | null;
    role_mandate?: string | null;
    context_notes?: string | null;
    additional_notes?: string | null;
  } | null;
  // Story #2245 — the previous round's synthesis (round_summary +
  // areas_to_probe + coverage gaps) embedded so each round's pack carries
  // forward what the prior round surfaced. Absent at round 1 and when no
  // materialised synthesis was available at compose time.
  prior_round?: {
    round_number?: number | null;
    round_summary?: string | null;
    areas_to_probe?: { topic?: string | null; rationale?: string | null }[] | null;
    gaps?:
      | {
          dimension_name?: string | null;
          note?: string | null;
          follow_up_questions?: string[] | null;
        }[]
      | null;
  } | null;
  candidate_summary?: {
    name?: string | null;
    overall_score?: number | null;
    recommendation?: string | null;
    summary?: string | null;
    competency_scores?: CompetencyScore[] | null;
  } | null;
  // Story #2317 — per-candidate briefing the recruiter wrote on this step,
  // surfaced so the interviewer sees the personalisation the pack composer
  // baked in.
  candidate_notes?: {
    content_markdown?: string | null;
  } | null;
  cv?: {
    raw_text?: string | null;
    structured?: unknown;
  } | null;
  interview_guide?: {
    kind?: string | null;
    content_markdown?: string | null;
  } | null;
  assessment_matrix?: {
    bands?: MatrixBand[] | null;
  } | null;
}

export interface InterviewPackVersionData {
  id: string;
  versionNumber: number;
  generationState: string;
  generationError: string | null;
  changeSummary: string | null;
  effectiveFrom: string;
  content: InterviewPackContent | null;
  author: { id: string; name: string };
}

export interface InterviewPackData {
  id: string;
  generationState: string | null;
  totalVersions: number;
  currentVersion: InterviewPackVersionData | null;
}

interface InterviewPackPanelProps {
  candidateSelectionStepId: string;
  pack: InterviewPackData | null;
  readOnly?: boolean;
  onChanged?: () => void;
}

const PROFILE_LABELS: Record<string, string> = {
  full: "Full pack",
  light: "Light pack",
};

export function InterviewPackPanel({
  candidateSelectionStepId,
  pack,
  readOnly = false,
  onChanged,
}: InterviewPackPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [{ fetching: generating }, generatePack] = useMutation(GENERATE_INTERVIEW_PACK);
  const [, exportToGoogleDocs] = useMutation(EXPORT_INTERVIEW_PACK_TO_GOOGLE_DOCS);

  const current = pack?.currentVersion ?? null;
  const isBuilding = current?.generationState === "generating";
  const isError = current?.generationState === "error";
  const isDraft = current?.generationState === "draft";
  const content = current?.content ?? null;
  const profileLabel = content?.profile ? (PROFILE_LABELS[content.profile] ?? null) : null;

  const handleGenerate = async (force: boolean) => {
    await generatePack({ input: { candidateSelectionStepId, force } });
    onChanged?.();
  };

  const handleExportStart = async () => {
    const result = await exportToGoogleDocs({ input: { candidateSelectionStepId } });
    const payload = result.data?.exportInterviewPackToGoogleDocs;
    return {
      documentExportId: payload?.documentExport?.id ?? null,
      errors: payload?.errors ?? [],
    };
  };

  return (
    <div
      className="mt-2 rounded border border-border bg-surface p-2"
      data-testid="interview-pack-panel"
    >
      <div className="flex flex-wrap items-center gap-2">
        <FileText size={12} className="text-text-faint" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
          Interview Pack
        </span>

        {isBuilding && (
          <span className="flex items-center gap-1 text-[11px] text-text-muted">
            <Loader2 size={12} className="animate-spin" /> Building pack…
          </span>
        )}

        {isDraft && (
          <span className="text-[11px] text-text-faint">
            v{current?.versionNumber}
            {profileLabel ? ` · ${profileLabel}` : ""}
          </span>
        )}

        {isError && (
          <span className="flex items-center gap-1 text-[11px] text-red-600">
            <AlertTriangle size={12} /> Generation failed
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {isDraft && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] font-medium text-text-secondary hover:bg-card-hover"
            >
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />} View
            </button>
          )}

          {isDraft && (
            <ExportButton
              onStart={handleExportStart}
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
            />
          )}

          {!readOnly && !isBuilding && (
            <button
              type="button"
              onClick={() => handleGenerate(current != null)}
              disabled={generating}
              className="flex items-center gap-1 rounded bg-accent px-2 py-1 text-[11px] font-medium text-white hover:bg-accent/90 disabled:opacity-50"
            >
              <Sparkles size={12} />
              {generating ? "Working…" : current != null ? "Regenerate" : "Generate interview pack"}
            </button>
          )}
        </div>
      </div>

      {isError && current?.generationError && (
        <p className="mt-1 text-[11px] text-red-600">{current.generationError}</p>
      )}

      {!current && !isBuilding && (
        <p className="mt-1 text-[11px] text-text-faint">
          No pack generated yet. Generate one to brief the interviewers.
        </p>
      )}

      {isDraft && expanded && content && <PackContentView content={content} />}
    </div>
  );
}

function PackContentView({ content }: { content: InterviewPackContent }) {
  const role = content.role_overview;
  const prior = content.prior_round;
  const summary = content.candidate_summary;
  const candidateNotes = content.candidate_notes;
  const cv = content.cv;
  const guide = content.interview_guide;
  const matrix = content.assessment_matrix;
  const competencyScores = summary?.competency_scores ?? [];
  const bands = matrix?.bands ?? [];
  const priorAreas = (prior?.areas_to_probe ?? []).filter(
    (a) => a != null && (a.topic != null || a.rationale != null),
  );
  const priorGaps = (prior?.gaps ?? []).filter((g) => g != null && g.note != null);

  return (
    <div className="mt-2 space-y-3 border-t border-border pt-2 text-xs text-text-secondary">
      {role && (
        <Section title="Role overview">
          {role.role_title && <p className="font-medium text-text-primary">{role.role_title}</p>}
          {role.additional_notes && (
            // The brief's additional notes are HTML authored via the rich-text
            // editor (see BriefPage), so render as markup rather than escaped text.
            <SanitizedHtml
              className="prose prose-sm dark:prose-invert max-w-none text-text-secondary"
              html={role.additional_notes}
            />
          )}
        </Section>
      )}

      {prior && (
        <Section title={`Prior round${prior.round_number != null ? ` ${prior.round_number}` : ""}`}>
          {prior.round_summary && (
            <p className="whitespace-pre-wrap text-text-secondary">{prior.round_summary}</p>
          )}
          {priorGaps.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                Coverage gaps from the prior round
              </p>
              <ul className="ml-4 list-disc space-y-0.5">
                {priorGaps.map((g, idx) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: list is replaced wholesale per pack version; no item identity to preserve
                  <li key={`prior-gap-${idx}`} className="text-text-secondary">
                    {g.dimension_name && (
                      <span className="font-medium text-text-primary">{g.dimension_name}</span>
                    )}
                    {g.dimension_name && g.note && <span> — </span>}
                    {g.note && <span>{g.note}</span>}
                    {(g.follow_up_questions ?? []).length > 0 && (
                      <ul className="ml-4 list-disc space-y-0.5 text-text-muted">
                        {(g.follow_up_questions ?? []).map((q, qIdx) => (
                          // biome-ignore lint/suspicious/noArrayIndexKey: list is replaced wholesale per pack version; no item identity to preserve
                          <li key={`prior-gap-${idx}-q-${qIdx}`}>{q}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {priorAreas.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                Areas to probe
              </p>
              <ul className="ml-4 list-disc space-y-0.5">
                {priorAreas.map((a, idx) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: list is replaced wholesale per pack version; no item identity to preserve
                  <li key={`prior-area-${idx}`} className="text-text-secondary">
                    {a.topic && <span className="font-medium text-text-primary">{a.topic}</span>}
                    {a.topic && a.rationale && <span> — </span>}
                    {a.rationale && <span>{a.rationale}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>
      )}

      {summary && (
        <Section title="Candidate summary">
          <p className="text-text-secondary">
            {summary.overall_score != null && (
              <span className="mr-2 font-medium text-text-primary">
                Score {summary.overall_score}
              </span>
            )}
            {summary.recommendation && <span>· {summary.recommendation}</span>}
          </p>
          {summary.summary && (
            <p className="whitespace-pre-wrap text-text-secondary">{summary.summary}</p>
          )}
          <PackRadar scores={competencyScores} candidateName={summary.name ?? null} />
          {competencyScores.length > 0 && (
            <table className="mt-1 w-full text-left text-[11px]">
              <thead className="text-text-faint">
                <tr>
                  <th className="py-0.5 pr-2 font-medium">Competency</th>
                  <th className="py-0.5 pr-2 font-medium">Ideal</th>
                  <th className="py-0.5 pr-2 font-medium">Score</th>
                  <th className="py-0.5 font-medium">Anchor</th>
                </tr>
              </thead>
              <tbody>
                {competencyScores.map((cs) => (
                  <tr
                    key={`${cs.dimension ?? "dim"}-${cs.band_level ?? cs.score ?? ""}`}
                    className="border-t border-border"
                  >
                    <td className="py-0.5 pr-2">{cs.dimension ?? "—"}</td>
                    <td className="py-0.5 pr-2">
                      {cs.ideal_score != null ? `${cs.ideal_score}/10` : "—"}
                    </td>
                    <td className="py-0.5 pr-2">{cs.score ?? "—"}</td>
                    <td className="py-0.5">{cs.anchor ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      )}

      {candidateNotes?.content_markdown && (
        <Section title="Pre-interview notes">
          <div className="prose prose-sm dark:prose-invert max-w-none text-text-secondary">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {candidateNotes.content_markdown}
            </ReactMarkdown>
          </div>
        </Section>
      )}

      {cv?.raw_text && (
        <Section title="CV">
          <p className="line-clamp-6 whitespace-pre-wrap text-text-secondary">{cv.raw_text}</p>
        </Section>
      )}

      {guide?.content_markdown && (
        <Section title="Interview guide">
          <div className="prose prose-sm dark:prose-invert max-w-none text-text-secondary">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{guide.content_markdown}</ReactMarkdown>
          </div>
        </Section>
      )}

      {bands.length > 0 && (
        <Section title="Assessment Matrix">
          <table className="w-full text-left text-[11px]">
            <thead className="text-text-faint">
              <tr>
                <th className="py-0.5 pr-2 font-medium">Dimension</th>
                <th className="py-0.5 pr-2 font-medium">Level</th>
                <th className="py-0.5 font-medium">Expectation</th>
              </tr>
            </thead>
            <tbody>
              {bands.map((band) => (
                <tr
                  key={`${band.dimension ?? "band"}-${band.level ?? ""}`}
                  className="border-t border-border"
                >
                  <td className="py-0.5 pr-2">{band.dimension ?? "—"}</td>
                  <td className="py-0.5 pr-2">{band.level ?? "—"}</td>
                  <td className="py-0.5">{band.body ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <h5 className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">{title}</h5>
      {children}
    </div>
  );
}

// Story #2255 — radar rendered from the frozen pack snapshot, mirroring the
// server-side SVG embedded in the exported Google Doc. Falls back silently
// when the snapshot lacks scored dimensions or continuity ids (pre-#2255
// packs); the Regenerate action will produce a fresh snapshot with the data.
function PackRadar({
  scores,
  candidateName,
}: {
  scores: CompetencyScore[];
  candidateName: string | null;
}) {
  const dimensions = scores
    .filter((cs) => cs.dimension != null && cs.continuity_id != null)
    .map((cs) => ({
      id: cs.continuity_id as string,
      continuityId: cs.continuity_id as string,
      name: cs.dimension as string,
      idealScore: cs.ideal_score ?? null,
    }));

  const candidateScores: Record<string, number> = {};
  for (const cs of scores) {
    if (cs.continuity_id != null && cs.score != null) candidateScores[cs.continuity_id] = cs.score;
  }

  if (dimensions.length === 0) return null;

  return (
    <div className="mt-2 flex justify-center">
      <RadarChart
        dimensions={dimensions}
        candidateScores={candidateScores}
        candidateName={candidateName ?? "Candidate"}
        size="sm"
      />
    </div>
  );
}
